import { useEffect, useState } from 'react';
import { CheckCircle2, ChevronRight, Info, RotateCcw, Save, UploadCloud, XCircle } from 'lucide-react';
import { Badge, Button, Card, CardContent, Input, Skeleton, Switch, cn, useToast } from '@respectly/ui';
import type { AiAgentConfigDto } from '@respectly/shared-types';
import { useAiOpsStore } from '../../stores/useAiOpsStore';
import { AGENT_USAGE_NOTES, OPS_META, PUBLIC_META, TENANT_META } from './format';

export function AgentsTab() {
  const { agents, loadingAgents, pricing, loadPricing } = useAiOpsStore();

  useEffect(() => {
    if (pricing.length === 0) void loadPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingAgents && agents.length === 0) {
    return <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>;
  }

  const ops = agents.filter((a) => a.category === 'ops');
  const tenant = agents.filter((a) => a.category === 'tenant_facing');
  const publicFacing = agents.filter((a) => a.category === 'public');

  return (
    <div className="space-y-8">
      {tenant.length > 0 && <Group title="Tenant-Facing Agents" color={TENANT_META.color} agents={tenant} />}
      {ops.length > 0 && <Group title="Ops Agents" color={OPS_META.color} agents={ops} />}
      {/* Last: the smallest group, and the only one talking to people who are not customers. */}
      {publicFacing.length > 0 && (
        <Group title="Website Chat" color={PUBLIC_META.color} agents={publicFacing} />
      )}
      {agents.length === 0 && (
        <p className="py-12 text-center text-sm text-slate-400">
          No agents configured. Run <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">npm run db:seed:ai-config</code>.
        </p>
      )}
    </div>
  );
}

function Group({ title, color, agents }: { title: string; color: string; agents: AiAgentConfigDto[] }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">{title}</h2>
        <span className="text-xs text-slate-400">({agents.length})</span>
      </div>
      <div className="space-y-3">
        {agents.map((a) => <AgentRow key={a.key} agent={a} />)}
      </div>
    </section>
  );
}

function AgentRow({ agent }: { agent: AiAgentConfigDto }) {
  const { pricing, updateAgent, resetAgent, syncAgent } = useAiOpsStore();
  const { toast } = useToast();
  const [model, setModel] = useState(agent.model);
  const [maxTokens, setMaxTokens] = useState(agent.maxTokens?.toString() ?? '');
  const [temperature, setTemperature] = useState(agent.temperature?.toString() ?? '');
  const [topP, setTopP] = useState(agent.topP?.toString() ?? '');
  const [active, setActive] = useState(agent.isActive);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptText, setPromptText] = useState(agent.systemPrompt ?? '');
  const [savingPrompt, setSavingPrompt] = useState(false);

  // Resync local edit state when the row is refreshed from the server.
  useEffect(() => {
    setModel(agent.model);
    setMaxTokens(agent.maxTokens?.toString() ?? '');
    setTemperature(agent.temperature?.toString() ?? '');
    setTopP(agent.topP?.toString() ?? '');
    setActive(agent.isActive);
    setPromptText(agent.systemPrompt ?? '');
  }, [agent.updatedAt, agent.model, agent.maxTokens, agent.temperature, agent.topP, agent.isActive, agent.systemPrompt]);

  const modelOptions = Array.from(
    new Set(pricing.filter((p) => p.provider === agent.provider && p.isActive).map((p) => p.model))
  );
  const dirty =
    model !== agent.model ||
    maxTokens !== (agent.maxTokens?.toString() ?? '') ||
    temperature !== (agent.temperature?.toString() ?? '') ||
    topP !== (agent.topP?.toString() ?? '') ||
    active !== agent.isActive;

  const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));

  const save = async () => {
    setSaving(true);
    try {
      await updateAgent(agent.key, {
        model,
        maxTokens: numOrNull(maxTokens),
        temperature: numOrNull(temperature),
        topP: numOrNull(topP),
        isActive: active,
      });
      toast({ title: 'Saved', description: `${agent.displayName} updated.` });
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : 'Try again', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      await resetAgent(agent.key);
      toast({ title: 'Reset to default', description: agent.displayName });
    } catch (err) {
      toast({ title: 'Reset failed', description: err instanceof Error ? err.message : 'Try again', variant: 'error' });
    }
  };

  const sync = async () => {
    setSyncing(true);
    const r = await syncAgent(agent.key);
    setSyncing(false);
    toast(
      r.ok
        ? { title: 'Applied to Foundry', description: `${agent.displayName} now uses ${agent.model}.` }
        : { title: 'Foundry sync failed', description: r.message ?? 'See sync error', variant: 'error' }
    );
  };

  const foundry = agent.provisioningMode === 'foundry';
  const dirtyPrompt = promptText !== (agent.systemPrompt ?? '');

  const savePrompt = async () => {
    setSavingPrompt(true);
    try {
      await updateAgent(agent.key, { systemPrompt: promptText });
      toast({
        title: 'System prompt saved',
        description: foundry ? 'Click “Apply to Foundry” to push it live.' : agent.displayName,
      });
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : 'Try again', variant: 'error' });
    } finally {
      setSavingPrompt(false);
    }
  };

  const revertPrompt = async () => {
    try {
      await updateAgent(agent.key, { systemPrompt: null });
      toast({ title: 'Reverted to default prompt', description: agent.displayName });
    } catch (err) {
      toast({ title: 'Revert failed', description: err instanceof Error ? err.message : 'Try again', variant: 'error' });
    }
  };

  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-slate-900 dark:text-slate-50">{agent.displayName}</h3>
              {agent.isModified && <Badge variant="outline" className="text-xs">modified</Badge>}
              <SyncBadge agent={agent} />
            </div>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{agent.description}</p>
            {AGENT_USAGE_NOTES[agent.key] && (
              <p className="mt-1 flex items-start gap-1.5 text-xs text-slate-400 dark:text-slate-500">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                <span>{AGENT_USAGE_NOTES[agent.key]}</span>
              </p>
            )}
            <div className="mt-1.5 flex gap-2 text-[11px] uppercase tracking-wide text-slate-400">
              <span>{agent.feature}</span>·<span>{agent.provider}</span>·<span>{agent.provisioningMode}</span>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            Enabled
            <Switch checked={active} onCheckedChange={setActive} />
          </label>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Model">
            {modelOptions.length > 0 ? (
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900"
              >
                {!modelOptions.includes(model) && <option value={model}>{model}</option>}
                {modelOptions.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            ) : (
              <Input value={model} onChange={(e) => setModel(e.target.value)} className="h-9" />
            )}
          </Field>
          <Field label="Max tokens">
            <Input type="number" value={maxTokens} placeholder="default" onChange={(e) => setMaxTokens(e.target.value)} className="h-9" />
          </Field>
          <Field label="Temperature">
            <Input type="number" step="0.1" min="0" max="2" value={temperature} placeholder="default" onChange={(e) => setTemperature(e.target.value)} className="h-9" />
          </Field>
          <Field label="Top-p">
            <Input type="number" step="0.05" min="0" max="1" value={topP} placeholder="default" onChange={(e) => setTopP(e.target.value)} className="h-9" />
          </Field>
        </div>

        {/* System prompt editor */}
        <div className="mt-4 border-t border-slate-100 pt-3 dark:border-slate-800">
          <button
            onClick={() => setPromptOpen((o) => !o)}
            className="flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300"
          >
            <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', promptOpen && 'rotate-90')} />
            System prompt
            {agent.systemPromptOverridden && <Badge variant="outline" className="text-[10px]">customized</Badge>}
          </button>
          {promptOpen && (
            <div className="mt-2 space-y-2">
              <textarea
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                rows={12}
                spellCheck={false}
                placeholder={
                  agent.systemPrompt
                    ? ''
                    : foundry
                      ? 'No default available here — managed in agent-fleet. Type to set an override.'
                      : 'Default captures the first time this agent runs. Type to set an override now.'
                }
                className="w-full rounded-md border border-slate-200 bg-white p-3 font-mono text-xs leading-relaxed text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
              {agent.systemPromptVariables.length > 0 && (
                <p className="text-xs text-slate-400">
                  Available variables:{' '}
                  {agent.systemPromptVariables.map((v) => (
                    <code key={v} className="mx-0.5 rounded bg-slate-100 px-1 dark:bg-slate-800">{`{{${v}}}`}</code>
                  ))}
                </p>
              )}
              <div className="flex gap-2">
                <Button size="sm" disabled={!dirtyPrompt || savingPrompt} onClick={savePrompt}>
                  <Save className="mr-1.5 h-3.5 w-3.5" /> {savingPrompt ? 'Saving…' : 'Save prompt'}
                </Button>
                {agent.systemPromptOverridden && (
                  <Button size="sm" variant="outline" onClick={revertPrompt}>
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Revert to default
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!dirty || saving} onClick={save}>
            <Save className="mr-1.5 h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button size="sm" variant="outline" onClick={reset}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset to default
          </Button>
          {foundry && (
            <Button size="sm" variant="outline" disabled={syncing} onClick={sync} title="Re-provision the Foundry agent with the saved model">
              <UploadCloud className="mr-1.5 h-3.5 w-3.5" /> {syncing ? 'Applying…' : 'Apply to Foundry'}
            </Button>
          )}
          {foundry && agent.syncStatus === 'pending' && (
            <span className="text-xs text-amber-600 dark:text-amber-400">Model change saved — click “Apply to Foundry” to push it.</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SyncBadge({ agent }: { agent: AiAgentConfigDto }) {
  if (agent.provisioningMode !== 'foundry') return null;
  if (agent.syncStatus === 'synced')
    return <Badge variant="outline" className="gap-1 border-emerald-300 text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="h-3 w-3" /> synced</Badge>;
  if (agent.syncStatus === 'pending')
    return <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700 dark:text-amber-400">pending</Badge>;
  return (
    <Badge variant="outline" className="gap-1 border-red-300 text-red-700 dark:text-red-400" title={agent.syncError ?? ''}>
      <XCircle className="h-3 w-3" /> error
    </Badge>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className={cn('mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400')}>{label}</span>
      {children}
    </label>
  );
}
